import React, { useState, useMemo } from 'react'
import { useChatContext } from 'stream-chat-react'
import type { Channel } from 'stream-chat'
import { useUserInfo } from 'hooks/useUserInfo'
import { useGetBusinessProfile, IAssigneeOptions } from 'appHooks/useGetBusinessProfile'
import {
  useGetAdvisorDashboardQuery,
  useGetBusinessProfileQuery,
  useGetMyProfileQuery,
} from '__generated__/api-types-and-hooks'
import { useTenantId } from 'utils/useTenantId'
import { USER_ROLES } from 'config'
import { useSelector } from 'react-redux'
import { RootState } from 'App'
import { AppAvatar } from 'components/Common/AppAvatar/AppAvatar.component'
import { getInitials } from 'components/Chat/helper'
import { useParams } from 'react-router-dom'
import { getTenantId } from 'utils/getTenantId'

interface UserSelectionProps {
  onBack: () => void
  onUserSelect: (channel: Channel) => void
  chatUsers: any
}

const UserSelection: React.FC<UserSelectionProps> = ({ onBack, onUserSelect, chatUsers }) => {
  const [searchQuery, setSearchQuery] = useState('')
  useUserInfo()
  const tenantId = useTenantId()
  const { clientId } = useParams()
  const loggedInUser = useSelector((state: RootState) => state.user.user)
  const userId = loggedInUser?.id
  const isAdvisor = loggedInUser?.roles?.includes(USER_ROLES.BSO_ADVISOR)
  const { client } = useChatContext()

  // Get business profile to access team members
  const { data: advisorData, isLoading: isAdvisorDataLoading } = useGetAdvisorDashboardQuery({
    tenantId,
  })
  const { assigneeOptions: businessAssigneeOptions } = useGetBusinessProfile({ enabled: true })

  // Get loading states from business profile queries (only when not an advisor)
  // These queries are also called by useGetBusinessProfile, but we need the loading states
  const { isLoading: isBusinessProfileLoading } = useGetBusinessProfileQuery(
    { id: clientId ?? userId ?? '' },
    { refetchOnWindowFocus: false, enabled: !isAdvisor && !!userId }
  )
  const { isLoading: isMyProfileLoading } = useGetMyProfileQuery(
    { userId: clientId ?? userId ?? '', tenantId: getTenantId() },
    { refetchOnWindowFocus: false, enabled: !isAdvisor && !!userId }
  )

  // Combine all loading states
  const isLoading = isAdvisor
    ? isAdvisorDataLoading
    : isBusinessProfileLoading || isMyProfileLoading

  // Memoize assignee options to prevent infinite re-renders and handle loading state
  const assigneeOptions = useMemo(() => {
    let options: IAssigneeOptions[] = []

    if (!isAdvisor) {
      options = businessAssigneeOptions || []
    } else {
      const response = JSON.parse(advisorData?.getAdvisorDashboard?.data || '{}')
      options =
        response?.accounts?.map((account: any) => ({
          label: `${account?.firstName} ${account?.lastName}`,
          value: account?.id,
          user: {
            id: account?.id,
            firstName: account?.firstName,
            lastName: account?.lastName,
          },
        })) || []
    }

    // Filter out current user
    return options.filter((option) => option.value !== loggedInUser?.id)
  }, [isAdvisor, businessAssigneeOptions, advisorData, loggedInUser?.id])

  const filteredUsers = assigneeOptions.filter((user) =>
    user.label.toLowerCase().includes(searchQuery.toLowerCase())
  )

  // Group users alphabetically
  const groupedUsers = filteredUsers.reduce((groups, user) => {
    const firstLetter = user.label.charAt(0).toUpperCase()
    if (!groups[firstLetter]) {
      groups[firstLetter] = []
    }
    groups[firstLetter].push(user)
    return groups
  }, {} as Record<string, IAssigneeOptions[]>)

  const sortedGroups = Object.keys(groupedUsers).sort()

  const handleUserSelect = async (selectedUserId: string) => {
    try {
      console.log('Starting user selection for:', selectedUserId)
      console.log('Current user ID:', client.userID)
      console.log('Active channels:', Object.keys(client.activeChannels))

      // Check if a channel already exists with this user
      const existingChannels = Object.values(client.activeChannels)
      const existingChannel = existingChannels.find((channel: any) => {
        const members = Object.values(channel.state.members || {})
        const memberIds = members.map((member: any) => (member as any)?.user?.id).filter(Boolean)

        // Check if channel has exactly 2 members: current user and selected user
        const hasExactlyTwoMembers = memberIds.length === 2
        const hasCurrentUser = memberIds.includes(client.userID)
        const hasSelectedUser = memberIds.includes(selectedUserId)
        const isCorrectDM = hasExactlyTwoMembers && hasCurrentUser && hasSelectedUser

        console.log(
          'Checking channel:',
          channel.cid,
          'memberIds:',
          memberIds,
          'hasExactlyTwoMembers:',
          hasExactlyTwoMembers,
          'hasCurrentUser:',
          hasCurrentUser,
          'hasSelectedUser:',
          hasSelectedUser,
          'isCorrectDM:',
          isCorrectDM
        )

        return isCorrectDM
      })

      if (existingChannel) {
        // Navigate to existing channel
        console.log('Navigating to existing channel:', existingChannel)

        // Ensure the existing channel is properly watched
        if (!existingChannel.state.isUpToDate) {
          await existingChannel.watch()
        }

        console.log('Active channel set, calling onUserSelect')
        onUserSelect(existingChannel)
        return
      }

      // Create new channel for direct message
      console.log('Creating new channel with user:', selectedUserId)

      const customData = {
        members: [selectedUserId, client.userID || 'unknown'],
        team: tenantId,
        // Ensure the same set of members always maps to the same channel
        distinct: true,
      }

      console.log('Creating direct message channel with data:', customData)

      // Let Stream generate a safe channel ID (avoids length limits)
      const channel = client.channel('messaging', customData)

      try {
        await channel.create()
        console.log('Channel created successfully')

        await channel.watch()
        console.log('Channel watched successfully')

        // Add a small delay to ensure the channel is properly initialized
        await new Promise((resolve) => setTimeout(resolve, 100))

        // Ensure the channel is properly initialized
        if (channel.state.isUpToDate) {
          console.log('Channel set as active, calling onUserSelect')
          onUserSelect(channel as unknown as Channel)
        } else {
          console.error('Channel not properly initialized')
        }
      } catch (createError) {
        console.error('Error in channel creation process:', createError)
      }
    } catch (error) {
      console.error('Error creating/navigating to channel:', error)
    }
  }

  return (
    <div className="h-full bg-app-white-100 flex flex-col">
      {/* Header */}
      <div className="px-4 py-4 flex items-center h-[70px]">
        <button
          onClick={onBack}
          className="p-2 hover:bg-app-grey-10 rounded-lg transition-colors duration-150"
        >
          <svg
            className="w-5 h-5 text-app-grey-70"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>
      </div>

      {/* Search Bar */}
      <div className="px-6 pb-4">
        <div className="relative">
          <input
            type="text"
            placeholder="Search by name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-4 pr-12 py-3 bg-app-grey-20 border border-app-grey-25 rounded-lg text-sm placeholder-app-grey-55 focus:outline-none focus:ring-2 focus:ring-app-primary-60 focus:border-transparent"
          />
          <svg
            className="absolute right-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-app-grey-55"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
      </div>

      {/* Users List */}
      <div className="flex-1 overflow-y-auto min-h-[calc(100vh-300px)]">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center h-64">
            <div className="w-16 h-16 bg-app-grey-20 rounded-full flex items-center justify-center mb-4">
              <svg
                className="w-8 h-8 text-app-grey-50 animate-spin"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </div>
            <p className="text-app-grey-60 text-center">Loading contacts...</p>
          </div>
        ) : sortedGroups.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64">
            <div className="w-16 h-16 bg-app-grey-20 rounded-full flex items-center justify-center mb-4">
              <svg
                className="w-8 h-8 text-app-grey-50"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
            </div>
            <p className="text-app-grey-60 text-center">
              {searchQuery ? `No contacts found for "${searchQuery}"` : 'No contacts found'}
            </p>
          </div>
        ) : (
          <div className="px-6">
            {sortedGroups.map((letter, index) => (
              <div key={letter}>
                <h3 className="text-sm font-semibold text-app-grey-60 mb-3 sticky top-0 bg-app-white-100 py-2">
                  {letter}
                </h3>
                <div className="space-y-1">
                  {groupedUsers[letter].map((user) => (
                    <button
                      key={user.value}
                      onClick={() => handleUserSelect(user.value)}
                      className="w-full flex items-center gap-3 px-3 py-3 text-left hover:bg-app-grey-10 rounded-lg transition-colors duration-150"
                    >
                      <AppAvatar
                        size="md"
                        type="mixed"
                        shape="Rounded"
                        text={getInitials({
                          firstName: user.user?.firstName || '',
                          lastName: user.user?.lastName || '',
                        })}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-app-black-100 truncate">
                          {user.label}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
                {/* Add divider between sections, but not after the last one */}
                {index < sortedGroups.length - 1 && (
                  <div className="my-6">
                    <hr className="border-app-grey-25" />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Center Message */}
    </div>
  )
}

export default UserSelection
